// Site-authored (not synced from the app): asks the site to copy this overlay's
// channel emotes into StreamNook's image store, so they still draw when 7TV,
// BTTV or FFZ is down (functions/utils/emotePrefill.ts does the work).
//
// One request a little after the page loads, repeated while the server says
// there is more to copy, then again every half hour so a switched 7TV set is
// warmed too. The server skips a channel it warmed recently, so a repeat costs
// it almost nothing. Nothing here affects drawing: a failed call is ignored.

const PREFILL_URL = '/api/v1/emote-prefill';
const START_DELAY_MS = 5_000;
const STEP_GAP_MS = 1_500;
const BUSY_GAP_MS = 30_000;
const RECHECK_MS = 30 * 60_000;
/** Calls per round at most (a full 1,000-emote set takes about eight). */
const MAX_STEPS = 24;
const OVERLAY_ID = /^[A-Za-z0-9]{6,32}$/;

interface PrefillAnswer {
  more?: boolean;
  channels?: Array<{ state?: string }>;
}

/** Start warming an overlay's emotes. Returns a disposer. */
export function startEmotePrefill(overlayId: string): () => void {
  if (!OVERLAY_ID.test(overlayId) || typeof fetch !== 'function') return () => {};
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let steps = 0;
  const later = (ms: number, run: () => void) => {
    if (stopped) return;
    timer = setTimeout(run, ms);
  };
  const step = async () => {
    if (stopped) return;
    steps += 1;
    let answer: PrefillAnswer | null = null;
    try {
      const res = await fetch(`${PREFILL_URL}?overlay=${encodeURIComponent(overlayId)}`, { method: 'POST' });
      answer = res.ok ? ((await res.json()) as PrefillAnswer) : null;
    } catch {
      answer = null;
    }
    if (stopped) return;
    if (answer?.more && steps < MAX_STEPS) {
      const busy = answer.channels?.some((c) => c.state === 'busy');
      later(busy ? BUSY_GAP_MS : STEP_GAP_MS, () => void step());
      return;
    }
    steps = 0;
    later(RECHECK_MS, () => void step());
  };
  later(START_DELAY_MS, () => void step());
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}
